import React, { useState, useEffect } from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Collapse,
    IconButton,
    Typography,
    Chip,
    Box,
    Divider,
    Card,
    CardContent,
    Grid,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneIcon from "@mui/icons-material/Phone";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CommentIcon from "@mui/icons-material/Comment";
import BusinessIcon from "@mui/icons-material/Business";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CallIcon from "@mui/icons-material/Call";
import moment from "moment";
import { FISH_FEED_FOLLOWUP_CALL } from "../../../constants";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";

const FollowupCalls = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Data states
    const [followupData, setFollowupData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openRows, setOpenRows] = useState([]);

    // Filter states
    const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [mobileFilter, setMobileFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [purposeFilter, setPurposeFilter] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    // State options for dropdown
    const stateOptions = [
        "AP", "OD", "TN", "RJ", "GJ", "HR", "PN",
        "KT", "WB", "CG"
    ];

    const statusOptions = ["Open", "Closed"];
    const purposeOptions = ["Feed Requirement", "Technical Service", "Feed Issue", "Service Issue", "Dealership Enquiry"];

    // Fetch followup calls data
    const fetchFollowupCalls = async () => {
        try {
            setLoading(true);
            setError(null);

            const queryParams = new URLSearchParams();
            queryParams.append("date", selectedDate);
            queryParams.append("page", page + 1);
            queryParams.append("pageSize", rowsPerPage);

            // Add filter parameters
            if (mobileFilter.trim()) {
                queryParams.append("mobileNumber", mobileFilter.trim());
            }
            if (stateFilter) {
                queryParams.append("state", stateFilter);
            }
            if (statusFilter) {
                queryParams.append("status", statusFilter);
            }
            if (purposeFilter) {
                queryParams.append("purpose", purposeFilter);
            }

            const response = await axios.get(
                `${FISH_FEED_FOLLOWUP_CALL}?${queryParams.toString()}`
            );

            if (response.data.success) {
                setFollowupData(response.data.data);
                setTotalCount(response.data.pagination.totalRecords);
            } else {
                setError("Failed to fetch followup calls");
            }
        } catch (err) {
            console.error("Error fetching followup calls:", err);
            setError("Error fetching followup calls. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Initial load and when dependencies change
    useEffect(() => {
        fetchFollowupCalls();
    }, [selectedDate, page, rowsPerPage, mobileFilter, stateFilter, statusFilter, purposeFilter]);

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle row expansion
    const handleRowToggle = (index) => {
        setOpenRows((prev) => {
            if (prev.includes(index)) {
                return prev.filter((rowIndex) => rowIndex !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    // Handle filter changes
    const handleClearFilters = () => {
        setMobileFilter("");
        setStateFilter("");
        setStatusFilter("");
        setPurposeFilter("");
        setPage(0);
    };

    const handleMobileFilterChange = (event) => {
        setMobileFilter(event.target.value);
        setPage(0);
    };

    const handleStateFilterChange = (event) => {
        setStateFilter(event.target.value);
        setPage(0);
    };

    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    const handlePurposeFilterChange = (event) => {
        setPurposeFilter(event.target.value);
        setPage(0);
    };

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
        setPage(0);
    };

    // Handle call action
    const handleCallClick = (row) => {
        navigate("/dashboard", {
            state: {
                mobNo: row.mobileNumber,
                lotNum: row.mobileNumber,
                followupId: row.id,
            },
        });
    };

    // Format date and time
    const formatDate = (dateString) =>
        moment(dateString).format("DD-MM-YYYY");

    const formatDateTime = (dateString) =>
        moment(dateString).format("DD-MM-YYYY HH:mm:ss");

    // Get status color
    const getStatusColor = (status) => {
        return status === "Open" ? "error" : "success";
    };

    // Get purpose color
    const getPurposeColor = (purpose) => {
        switch (purpose) {
            case "Feed Requirement": return "primary";
            case "Service Issue": return "warning";
            case "Product Information": return "info";
            default: return "default";
        }
    };

    // Action button component
    const ActionButton = ({ row }) => (
        <Button
            variant="contained"
            size="small"
            startIcon={<CallIcon />}
            onClick={() => handleCallClick(row)}
            color="info"
            sx={{
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 1,
                '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
            }}
        >
            Call
        </Button>
    );

    // Info item component for details
    const InfoItem = ({ icon, label, value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 140,
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 500
            }}>
                {icon}
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                    {label}:
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 1, fontWeight: 400 }}>
                {value || "-"}
            </Typography>
        </Box>
    );

    const activeFiltersCount = [mobileFilter, stateFilter, statusFilter, purposeFilter].filter(Boolean).length;

    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Header with Date Selector and Filters */}
            <Box sx={{ p: 2, backgroundColor: 'info.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                        <ScheduleIcon sx={{ mr: 1, color: 'info.main' }} />
                        Todays Follow-up Calls
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            type="date"
                            label="Follow-up Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 200 }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            size="small"
                            color="info"
                            sx={{ textTransform: 'none' }}
                        >
                            {showFilters ? 'Hide Filters' : `Show Filters${activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}`}
                        </Button>
                    </Box>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            label="Mobile Number"
                            value={mobileFilter}
                            onChange={handleMobileFilterChange}
                            placeholder="Enter mobile number"
                            size="small"
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ minWidth: 200 }}
                        />

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>State</InputLabel>
                            <Select
                                value={stateFilter}
                                onChange={handleStateFilterChange}
                                label="State"
                            >
                                <MenuItem value="">
                                    <em>All States</em>
                                </MenuItem>
                                {stateOptions.map((state) => (
                                    <MenuItem key={state} value={state}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">
                                    <em>All Status</em>
                                </MenuItem>
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Purpose</InputLabel>
                            <Select
                                value={purposeFilter}
                                onChange={handlePurposeFilterChange}
                                label="Purpose"
                            >
                                <MenuItem value="">
                                    <em>All Purposes</em>
                                </MenuItem>
                                {purposeOptions.map((purpose) => (
                                    <MenuItem key={purpose} value={purpose}>
                                        {purpose}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {activeFiltersCount > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                size="small"
                                color="error"
                                sx={{ textTransform: 'none' }}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Box>

                    {/* Active filters display */}
                    {activeFiltersCount > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                                Active Filters:
                            </Typography>
                            {mobileFilter && (
                                <Chip
                                    label={`Mobile: ${mobileFilter}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    onDelete={() => setMobileFilter("")}
                                />
                            )}
                            {stateFilter && (
                                <Chip
                                    label={`State: ${stateFilter}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    onDelete={() => setStateFilter("")}
                                />
                            )}
                            {statusFilter && (
                                <Chip
                                    label={`Status: ${statusFilter}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    onDelete={() => setStatusFilter("")}
                                />
                            )}
                            {purposeFilter && (
                                <Chip
                                    label={`Purpose: ${purposeFilter}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    onDelete={() => setPurposeFilter("")}
                                />
                            )}
                        </Box>
                    )}
                </Collapse>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Loading Indicator */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Table */}
            {!loading && (
                <TableContainer component={Paper} elevation={0}>
                    <Table
                        sx={{
                            minWidth: 650,
                            height: "100%",
                            tableLayout: "fixed",
                        }}
                        aria-label="followup calls table"
                    >
                        <TableHead>
                            <TableRow sx={{
                                backgroundColor: "info.main",
                                '& .MuiTableCell-head': {
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                }
                            }}>
                                <TableCell width={60} sx={{ textAlign: "center" }} />
                                <TableCell width={60} sx={{ textAlign: "center", padding: 2 }}>
                                    S.No
                                </TableCell>
                                <TableCell width={140} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Mobile Number
                                    </Box>
                                </TableCell>
                                <TableCell width={160} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Customer Name
                                    </Box>
                                </TableCell>
                                <TableCell width={140} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />
                                        District
                                    </Box>
                                </TableCell>
                                <TableCell width={80} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />
                                        State
                                    </Box>
                                </TableCell>
                                <TableCell width={140} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CommentIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Purpose
                                    </Box>
                                </TableCell>
                                <TableCell width={100} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CommentIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Status
                                    </Box>
                                </TableCell>
                                <TableCell width={120} sx={{ textAlign: "center", padding: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Follow-up Date
                                    </Box>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "120px",
                                        position: "sticky",
                                        right: 0,
                                        backgroundColor: "info.main",
                                        zIndex: 100,
                                        boxShadow: "-2px 0 4px rgba(0,0,0,0.1)",
                                        padding: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CallIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Action
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(followupData) && followupData.length > 0 ? (
                                followupData.map((row, rowIndex) => (
                                    <React.Fragment key={row.id}>
                                        <TableRow
                                            sx={{
                                                "&:hover": { backgroundColor: "action.hover" },
                                                backgroundColor: rowIndex % 2 === 0 ? "grey.50" : "white",
                                                transition: 'background-color 0.2s ease',
                                            }}
                                        >
                                            <TableCell sx={{ padding: 1 }}>
                                                <IconButton
                                                    onClick={() => handleRowToggle(rowIndex)}
                                                    sx={{
                                                        padding: 1,
                                                        transition: 'transform 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'info.main',
                                                            color: 'white',
                                                            transform: 'scale(1.1)',
                                                        }
                                                    }}
                                                >
                                                    {openRows.includes(rowIndex) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2, fontWeight: 500 }}>
                                                <Chip
                                                    label={page * rowsPerPage + rowIndex + 1}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2, fontFamily: 'monospace' }}>
                                                {row.mobileNumber || "-"}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2, fontWeight: 500 }}>
                                                {row.name || "-"}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2 }}>
                                                {row.district || "-"}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2 }}>
                                                <Chip
                                                    label={row.state || "-"}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2 }}>
                                                <Chip
                                                    label={row.purpose || "-"}
                                                    size="small"
                                                    color={getPurposeColor(row.purpose)}
                                                    variant="filled"
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2 }}>
                                                <Chip
                                                    label={row.status || "-"}
                                                    size="small"
                                                    color={getStatusColor(row.status)}
                                                    variant="filled"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center", padding: 2, fontSize: '0.875rem' }}>
                                                {formatDate(row.followUpDate)}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    width: "120px",
                                                    padding: 2,
                                                    position: "sticky",
                                                    right: 0,
                                                    backgroundColor: rowIndex % 2 === 0 ? "grey.50" : "white",
                                                    zIndex: 10,
                                                    boxShadow: "-2px 0 4px rgba(0,0,0,0.1)",
                                                    transition: 'background-color 0.2s ease',
                                                }}
                                            >
                                                <ActionButton row={row} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                colSpan={10}
                                                sx={{ paddingBottom: 0, paddingTop: 0, border: 0 }}
                                            >
                                                <Collapse
                                                    in={openRows.includes(rowIndex)}
                                                    timeout="auto"
                                                    unmountOnExit
                                                >
                                                    <Box sx={{ margin: 2 }}>
                                                        <Card elevation={1} sx={{ borderRadius: 2 }}>
                                                            <CardContent sx={{ p: 3 }}>
                                                                <Typography variant="h6" sx={{
                                                                    mb: 2,
                                                                    color: 'info.main',
                                                                    fontWeight: 600,
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <BusinessIcon sx={{ mr: 1 }} />
                                                                    Past Remarks
                                                                </Typography>
                                                                <Divider sx={{ mb: 2 }} />

                                                                <Grid container spacing={3}>
                                                                    <Grid item xs={12} md={6}>
                                                                        <InfoItem
                                                                            icon={<PersonIcon fontSize="small" />}
                                                                            label="Customer Name"
                                                                            value={row.name}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<PhoneIcon fontSize="small" />}
                                                                            label="Mobile Number"
                                                                            value={row.mobileNumber}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<LocationOnIcon fontSize="small" />}
                                                                            label="District"
                                                                            value={row.district}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<LocationOnIcon fontSize="small" />}
                                                                            label="State"
                                                                            value={row.state}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<LocationOnIcon fontSize="small" />}
                                                                            label="Postal Code"
                                                                            value={row.postalCode}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<CommentIcon fontSize="small" />}
                                                                            label="Category"
                                                                            value={row.prospectFarmerCategory}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={6}>
                                                                        <InfoItem
                                                                            icon={<CommentIcon fontSize="small" />}
                                                                            label="Purpose"
                                                                            value={row.purpose}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<CommentIcon fontSize="small" />}
                                                                            label="Current Feed Used"
                                                                            value={row.currentFeedUsed}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<CommentIcon fontSize="small" />}
                                                                            label="Is Stocking"
                                                                            value={row.isStocking}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<CalendarTodayIcon fontSize="small" />}
                                                                            label="Follow-up Date"
                                                                            value={formatDate(row.followUpDate)}
                                                                        />
                                                                        <InfoItem
                                                                            icon={<CalendarTodayIcon fontSize="small" />}
                                                                            label="Created At"
                                                                            value={formatDateTime(row.createdAt)}
                                                                        /><InfoItem
                                                                            icon={<CalendarTodayIcon fontSize="small" />}
                                                                            label="Agent Remarks"
                                                                            value={row.agentRemarks}
                                                                        />
                                                                    </Grid>
                                                                </Grid>

                                                                {row.masterDetail && (
                                                                    <>
                                                                        <Typography variant="h6" sx={{
                                                                            mt: 3,
                                                                            mb: 2,
                                                                            color: 'info.main',
                                                                            fontWeight: 600,
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            <PersonIcon sx={{ mr: 1 }} />
                                                                            Customer Details
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />
                                                                        <Grid container spacing={2}>
                                                                            <Grid item xs={12} md={6}>
                                                                                <InfoItem
                                                                                    label="Farmer Village"
                                                                                    value={row.masterDetail.farmerVillage}
                                                                                />
                                                                                <InfoItem
                                                                                    label="Region"
                                                                                    value={row.masterDetail.region}
                                                                                />
                                                                                <InfoItem
                                                                                    label="Shrimp Zone"
                                                                                    value={row.masterDetail.shrimpZone}
                                                                                />
                                                                                <InfoItem
                                                                                    label="Dealer Name"
                                                                                    value={row.masterDetail.dealerName}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6}>
                                                                                <InfoItem
                                                                                    label="Dealer Code"
                                                                                    value={row.masterDetail.dealerCode}
                                                                                />
                                                                                <InfoItem
                                                                                    label="Feed Type"
                                                                                    value={row.masterDetail.feedType}
                                                                                />
                                                                                <InfoItem
                                                                                    label="Farmer Type"
                                                                                    value={row.masterDetail.farmerType}
                                                                                />
                                                                                <InfoItem
                                                                                    label="Employee Code"
                                                                                    value={row.masterDetail.employeeCode}
                                                                                />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            color: 'text.secondary'
                                        }}>
                                            <ScheduleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: 'info.main' }} />
                                            <Typography variant="h6" sx={{ mb: 1 }}>No Follow-up Calls</Typography>
                                            <Typography variant="body2">There are no follow-up calls scheduled for {formatDate(selectedDate)}.</Typography>
                                            {activeFiltersCount > 0 && (
                                                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                    Try adjusting your filters to see more results.
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {!loading && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'grey.50'
                    }}
                />
            )}
        </Paper>
    );
};

export default FollowupCalls;